import { RealtimePostgresChangesPayload, SupabaseClient } from '@supabase/supabase-js';
import { InAppNotification } from '../api/Types';
import { SupabaseService } from './SupabaseClient';
import { serviceMappers } from './serviceMappers';
import { IN_APP_NOTIFICATION_WITH_ACTOR_SELECT } from './serviceSelectors';

interface RawInAppNotificationRow {
  id: number;
  event_type: InAppNotification['event_type'];
  recipe_id: string | null;
  comment_id: number | null;
  realization_id: number | null;
  title: string;
  body: string | null;
  route_path: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  actor:
    | {
        id: number;
        name: string;
        first_name: string;
        avatar_url: string | null;
      }
    | {
        id: number;
        name: string;
        first_name: string;
        avatar_url: string | null;
      }[]
    | null;
}

export class InAppNotificationService extends SupabaseService {
  private throwIfQueryFailed(error: { message?: string } | null): void {
    if (error) {
      throw new Error(error.message);
    }
  }

  private normalizeNotificationRows(data: unknown): InAppNotification[] {
    return serviceMappers.mapNotificationRows(((data || []) as RawInAppNotificationRow[]));
  }

  private countUpdatedRows(data: Array<{ id: number }> | null): number {
    return (data || []).length;
  }

  private async markUnreadNotificationsAsRead(notificationId?: number): Promise<number> {
    const supabaseClient = await this.getClient();
    const readAt = new Date().toISOString();
    const updateQuery = supabaseClient
      .from('in_app_notifications')
      .update({ is_read: true, read_at: readAt });
    const scopedQuery = notificationId == null
      ? updateQuery.eq('is_read', false)
      : updateQuery.eq('id', notificationId).eq('is_read', false);
    const { data, error } = await scopedQuery.select('id');

    this.throwIfQueryFailed(error);

    return this.countUpdatedRows(data as Array<{ id: number }> | null);
  }

  public async listMyNotifications(limit: number = 20): Promise<InAppNotification[]> {
    const supabaseClient = await this.getClient();
    const { data, error } = await supabaseClient
      .from('in_app_notifications')
      .select(IN_APP_NOTIFICATION_WITH_ACTOR_SELECT)
      .order('created_at', { ascending: false })
      .limit(limit);

    this.throwIfQueryFailed(error);

    return this.normalizeNotificationRows(data);
  }

  public async getUnreadCount(): Promise<number> {
    const supabaseClient = await this.getClient();
    const { count, error } = await supabaseClient
      .from('in_app_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);

    this.throwIfQueryFailed(error);

    return count ?? 0;
  }

  public async markAsRead(notificationId: number): Promise<boolean> {
    return (await this.markUnreadNotificationsAsRead(notificationId)) > 0;
  }

  public async markAllAsRead(): Promise<number> {
    return this.markUnreadNotificationsAsRead();
  }

  public async createRealtimeClient(): Promise<SupabaseClient> {
    return this.getClient();
  }
}

export type InAppNotificationRealtimePayload = RealtimePostgresChangesPayload<RawInAppNotificationRow>;

export const inAppNotificationService = new InAppNotificationService();
